import React, { useEffect, useMemo, useState } from "react";
import { api, formatError } from "@/lib/api";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import TypeAhead from "@/components/TypeAhead";
import { PackageMinus, Trash2, Plus } from "lucide-react";

export default function Issue() {
  const [item, setItem] = useState(null);
  const [qtyRequested, setQtyRequested] = useState("");
  const [preview, setPreview] = useState(null);
  const [allocations, setAllocations] = useState([]);
  const [packagingChoice, setPackagingChoice] = useState("packaged");
  const [pkgLines, setPkgLines] = useState([{ item: null, count: "", source_location_id: "", override_negative: false }]);
  const [customerRef, setCustomerRef] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [locations, setLocations] = useState([]);

  useEffect(() => {
    api.get("/locations").then((r) => setLocations(r.data.filter(l => l.active))).catch(() => {});
  }, []);

  useEffect(() => { setPreview(null); setAllocations([]); }, [item]);

  const doPreview = async () => {
    if (!item || !Number(qtyRequested)) return;
    try {
      const { data } = await api.get("/issues/fifo-preview", {
        params: { item_id: item.id, qty: Number(qtyRequested) },
      });
      setPreview(data);
      setAllocations(data.allocations.map(a => ({ ...a, mode: "draw", qty: a.qty })));
    } catch (e) { toast.error(formatError(e)); }
  };

  const totalAllocated = useMemo(
    () => allocations.reduce((s, a) => s + (Number(a.qty) || 0), 0),
    [allocations],
  );

  const updateAlloc = (i, patch) =>
    setAllocations((A) => A.map((a, ix) => ix === i ? { ...a, ...patch } : a));

  const updatePkg = (i, patch) =>
    setPkgLines((P) => P.map((p, ix) => ix === i ? { ...p, ...patch } : p));
  const addPkg = () => setPkgLines((P) => [...P, { item: null, count: "", source_location_id: "", override_negative: false }]);
  const rmPkg = (i) => setPkgLines((P) => P.filter((_, ix) => ix !== i));

  const submit = async () => {
    if (!item || allocations.length === 0) return;
    setBusy(true);
    try {
      const payload = {
        item_id: item.id,
        allocations: allocations.map(a => ({
          container_id: a.container_id,
          mode: a.mode,
          qty: Number(a.qty) || 0,
        })),
        packaging_choice: packagingChoice,
        packaging: packagingChoice === "packaged"
          ? pkgLines.filter(p => p.item && Number(p.count) > 0).map(p => ({
              item_id: p.item.id,
              count: Number(p.count),
              source_location_id: p.source_location_id,
              override_negative: !!p.override_negative,
            }))
          : [],
        customer_ref: customerRef || null,
        note: note || null,
      };
      const { data } = await api.post("/issues", payload);
      toast.success(
        data.recount_flags?.length
          ? "Issued (packaging recount flagged)"
          : "Issue posted",
      );
      setItem(null); setQtyRequested(""); setPreview(null);
      setAllocations([]); setCustomerRef(""); setNote("");
      setPkgLines([{ item: null, count: "", source_location_id: "", override_negative: false }]);
    } catch (e) { toast.error(formatError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-6 max-w-4xl animate-fade-in" data-testid="issue-form">
      <div>
        <div className="data-label">Outward</div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold">Issue Stock</h1>
      </div>

      <div className="bg-white border border-border rounded-sm p-4 sm:p-6 space-y-4">
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <span className="data-label">Item</span>
            <div className="mt-1">
              <TypeAhead
                endpoint="/items/search"
                value={item} onChange={setItem}
                subLabelKey="sku" testid="issue-item"
              />
            </div>
          </div>
          <div>
            <span className="data-label">Requested quantity</span>
            <div className="mt-1 flex gap-2">
              <input
                data-testid="issue-qty"
                type="number" step="0.001"
                className="field font-mono"
                value={qtyRequested}
                onChange={(e) => setQtyRequested(e.target.value)}
              />
              <button data-testid="issue-preview-btn" onClick={doPreview}
                      className="btn-ghost min-w-[110px]">FIFO preview</button>
            </div>
          </div>
        </div>

        {preview && (
          <div className="border border-border rounded-sm">
            <div className="px-4 py-2 border-b border-border flex items-center justify-between">
              <span className="data-label">Allocation (oldest batch first — override any row)</span>
              {preview.shortfall > 0 && (
                <span className="text-xs text-destructive font-semibold">Short by {preview.shortfall}</span>
              )}
            </div>
            <ul className="divide-y divide-border" data-testid="issue-allocations">
              {allocations.map((a, i) => (
                <li key={a.container_id} className="p-3 space-y-2" data-testid={`issue-alloc-${i}`}>
                  <div className="flex items-center gap-3 justify-between">
                    <div className="min-w-0">
                      <div className="container-code truncate">{a.container_code}</div>
                      <div className="text-xs text-muted-foreground truncate">
                        {a.batch_number} · {a.description}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="data-label">Available</div>
                      <div className="font-mono font-semibold">{a.available}</div>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2 items-center">
                    <select
                      className="field-sm w-32"
                      value={a.mode}
                      onChange={(e) => updateAlloc(i, {
                        mode: e.target.value,
                        qty: e.target.value === "whole" ? a.available : a.qty,
                      })}
                      data-testid={`issue-alloc-${i}-mode`}
                    >
                      <option value="draw">Draw</option>
                      <option value="whole">Whole container</option>
                    </select>
                    <input
                      type="number" step="0.001"
                      className="field-sm w-32 font-mono"
                      value={a.qty}
                      disabled={a.mode === "whole"}
                      onChange={(e) => updateAlloc(i, { qty: e.target.value })}
                      data-testid={`issue-alloc-${i}-qty`}
                    />
                    <button
                      className="text-muted-foreground hover:text-destructive"
                      onClick={() => setAllocations(A => A.filter((_, ix) => ix !== i))}
                      data-testid={`issue-alloc-${i}-rm`}
                    ><Trash2 className="w-4 h-4" /></button>
                  </div>
                </li>
              ))}
            </ul>
            <div className="px-4 py-2 border-t border-border flex justify-between text-sm">
              <span className="text-muted-foreground">Total to draw</span>
              <span className="font-mono font-bold" data-testid="issue-total">{totalAllocated.toFixed(3)}</span>
            </div>
          </div>
        )}

        <div className="border-t border-border pt-4 space-y-3">
          <span className="data-label">Packed into?</span>
          <div className="flex flex-wrap gap-2">
            {[
              ["packaged", "Packaging item(s)"],
              ["customer", "Customer's packaging"],
              ["original", "Original container (whole)"],
            ].map(([v, l]) => (
              <button
                key={v}
                type="button"
                data-testid={`issue-pack-${v}`}
                onClick={() => setPackagingChoice(v)}
                className={`px-3 py-2 rounded-sm text-xs uppercase tracking-widest font-semibold border ${
                  packagingChoice === v
                    ? "bg-primary text-primary-foreground border-primary"
                    : "bg-white border-border text-foreground hover:bg-muted"
                }`}
              >{l}</button>
            ))}
          </div>
          {packagingChoice === "packaged" && (
            <div className="space-y-2">
              {pkgLines.map((p, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-start" data-testid={`issue-pkg-${i}`}>
                  <div className="col-span-5">
                    <TypeAhead
                      endpoint="/items/search"
                      value={p.item} onChange={(v) => updatePkg(i, { item: v })}
                      subLabelKey="sku" placeholder="Packaging item"
                      testid={`issue-pkg-${i}-item`}
                    />
                  </div>
                  <input
                    type="number" step="1"
                    placeholder="Count"
                    className="field-sm col-span-2 font-mono"
                    value={p.count}
                    onChange={(e) => updatePkg(i, { count: e.target.value })}
                    data-testid={`issue-pkg-${i}-count`}
                  />
                  <select
                    className="field-sm col-span-4"
                    value={p.source_location_id}
                    onChange={(e) => updatePkg(i, { source_location_id: e.target.value })}
                    data-testid={`issue-pkg-${i}-loc`}
                  >
                    <option value="">Source location</option>
                    {locations.map(l => <option key={l.id} value={l.id}>{l.code}</option>)}
                  </select>
                  <button
                    onClick={() => rmPkg(i)}
                    className="col-span-1 text-muted-foreground hover:text-destructive pt-3"
                    data-testid={`issue-pkg-${i}-rm`}
                  ><Trash2 className="w-4 h-4" /></button>
                  <label className="col-span-12 text-xs flex items-center gap-2 text-muted-foreground">
                    <input type="checkbox" checked={p.override_negative}
                      onChange={(e) => updatePkg(i, { override_negative: e.target.checked })}
                      data-testid={`issue-pkg-${i}-override`}
                    />
                    Override negative stock (recount flag)
                  </label>
                </div>
              ))}
              <button type="button" onClick={addPkg}
                      data-testid="issue-pkg-add"
                      className="text-primary text-xs font-semibold uppercase tracking-widest flex items-center gap-1">
                <Plus className="w-4 h-4" /> Add packaging line
              </button>
            </div>
          )}
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <span className="data-label">Customer / ref</span>
            <input className="field mt-1" value={customerRef} onChange={(e) => setCustomerRef(e.target.value)}
                   data-testid="issue-customer" />
          </div>
          <div>
            <span className="data-label">Note</span>
            <input className="field mt-1" value={note} onChange={(e) => setNote(e.target.value)}
                   data-testid="issue-note" />
          </div>
        </div>
      </div>

      <div className="sticky bottom-0 z-10 -mx-4 sm:mx-0 px-4 sm:px-5 py-3 border-t sm:border sm:rounded-sm bg-white border-border">
        <div className="flex justify-between items-center">
          <div>
            <div className="data-label">Total to issue</div>
            <div className="font-mono text-xl font-bold">{totalAllocated.toFixed(3)}</div>
          </div>
          <button data-testid="issue-submit" onClick={submit}
                  disabled={busy || allocations.length === 0}
                  className="btn-primary">
            <PackageMinus className="w-4 h-4 mr-2" />
            {busy ? "Posting…" : "Post issue"}
          </button>
        </div>
      </div>
    </div>
  );
}
